import { SectionCard } from '@/components/SectionCard/SectionCard'
import { hivesOverviewOrganizationDetailsRoute } from '@/features/HivesOverview/pages/HivesOverviewOrganizationDetails'
import type { ResolutionDescription } from '@/features/SalesforceIngest/lib/describeResolution'
import { Button, Link as ChakraLink, DataList } from '@chakra-ui/react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

export type AcceptedResolution = {
  ingestId: string
  opportunityId: string
  description: ResolutionDescription
}

type Props = {
  accepted: AcceptedResolution
  onResolveAnother: () => void
}

/**
 * What replaces the form once the endpoint has answered 202.
 *
 * The endpoint is async by design: it validates, acknowledges with an
 * `ingestId` and orchestrates fire-and-forget, because delivering the
 * licences is up to 100 sequential calls. So "accepted" is genuinely not
 * "done", and the operator learns the outcome from Slack rather than from
 * this page.
 *
 * Which Slack channel is not knowable here. A delivered resolution goes to
 * the handover channel; a stop or a failure goes to needs-attention. At 202
 * the orchestration has not run, so the copy names both rather than promising
 * one.
 *
 * `alertStatus` is `info`, not `success`. Green read as "it worked" and
 * invited people to skip the sentence explaining that it had not necessarily
 * worked, which is the one thing this panel exists to say.
 *
 * That is surprising enough to need a durable place on screen. A toast would
 * say it for four seconds and take the `ingestId` with it. Replacing the form
 * also rules out a double submit: the backend deduplicates by counting
 * licences already carrying `reference = opportunityId`, but safe is not a
 * reason to invite one.
 */
export function ResolutionReceipt({
  accepted,
  onResolveAnother,
}: Readonly<Props>) {
  const { t } = useTranslation('features', { useSuspense: false })

  return (
    <SectionCard
      title={String(t('salesforceIngest.receipt.title'))}
      alertStatus="info"
      alertDescription={String(t('salesforceIngest.receipt.notAResult'))}
      actions={
        <Button variant="outline" onClick={onResolveAnother}>
          {String(t('salesforceIngest.receipt.resolveAnother'))}
        </Button>
      }
    >
      <DataList.Root orientation="horizontal" divideY="1px" size="sm">
        <DataList.Item pt={2}>
          <DataList.ItemLabel>
            {String(t('salesforceIngest.receipt.ingestId'))}
          </DataList.ItemLabel>
          <DataList.ItemValue fontFamily="mono">
            {accepted.ingestId}
          </DataList.ItemValue>
        </DataList.Item>

        <DataList.Item pt={2}>
          <DataList.ItemLabel>
            {String(t('salesforceIngest.receipt.opportunityId'))}
          </DataList.ItemLabel>
          <DataList.ItemValue fontFamily="mono">
            {accepted.opportunityId}
          </DataList.ItemValue>
        </DataList.Item>

        {/* Only when the operator named one. On the create-a-new-org cases
            the id is minted by the orchestration and never reaches this page,
            so there is nothing to link to. */}
        {accepted.description.values.orgId ? (
          <DataList.Item pt={2}>
            <DataList.ItemLabel>
              {String(t('salesforceIngest.receipt.organization'))}
            </DataList.ItemLabel>
            <DataList.ItemValue>
              <ChakraLink asChild fontFamily="mono" colorPalette="brand">
                <Link
                  to={hivesOverviewOrganizationDetailsRoute.to}
                  params={{ orgId: accepted.description.values.orgId }}
                >
                  {accepted.description.values.orgId}
                </Link>
              </ChakraLink>
            </DataList.ItemValue>
          </DataList.Item>
        ) : null}

        <DataList.Item pt={2}>
          <DataList.ItemLabel>
            {String(t('salesforceIngest.receipt.submitted'))}
          </DataList.ItemLabel>
          <DataList.ItemValue>
            {String(
              t(
                `salesforceIngest.case.${accepted.description.caseKey}`,
                accepted.description.values,
              ),
            )}
          </DataList.ItemValue>
        </DataList.Item>
      </DataList.Root>
    </SectionCard>
  )
}
